import {DictionaryItem} from './dictionary.model';
import {PartnerIdentity} from './partner.model';
import {LocalizationIdentity} from './localization.model';
import {InsuredProduct, InsuredProductCpi, InsuredProductPg, ProductIdentity, ProductVariantIdentity} from './product.model';
import {Party} from './party.model';
import {GenericEntity} from './generic-entity.model';
import {SystemUser} from './system-user.model';

export class Policy extends GenericEntity {
    attributeList: { code: string, stringValue: string }[];
    installmentList: { amount: number, installmentDate: string }[];
    insuranceEndDate: string;
    insuranceStartDate: string;
    insuredPerson: InsuredPerson;
    insuredProduct: InsuredProduct;
    issueDate: string;
    localization: LocalizationIdentity;
    number: string;
    operationList: PolicyOperation[];
    partner: PartnerIdentity;
    paymentDefinition: PaymentDefinition;
    policyholder: Policyholder;
    premium: number;
    product: ProductIdentity;
    refundAmount: number;
    refundBankAccount: string;
    riskList: PolicyRisk[];
    seller: SystemUser;
    status: DictionaryItem;
    statusDate: string;
    statusReason: DictionaryItem;
    variant: ProductVariantIdentity;

    constructor(props = {}) {
        super(props);
        Object.assign(this, props);
    }

    getAttributeListItem(code: string) {
        return this.attributeList?.find(item => item.code === code)?.stringValue;
    }

    isNaturalPerson() {
        return this.insuredPerson?.party?.partyType?.code === 'NATURAL_PERSON';
    }

    getTransferTitle() {
        if (this.isNaturalPerson()) {
            return `${this.number} ${this.insuredPerson?.party?.firstName} ${this.insuredPerson?.party?.lastName}`;
        } else {
            return `${this.number} ${this.insuredPerson?.party?.companyName}`;

        }
    }
}

export class PolicyCpi extends Policy {
    insuredProduct: InsuredProductCpi;
}

export class PolicyPg extends Policy {
    insuredProduct: InsuredProductPg;
    type: PolicyPgType;

    constructor(props = {}) {
        super(props);
        Object.assign(this, props);
    }

    isOperationEnabled(operation: PolicyOperation) {
        return this.operationList?.indexOf(operation) !== -1;
    }

    isPaymentOnce() {
        return this.type === PolicyPgType.PG_SINGLE || (this.type === null && this.installmentList?.length === 1);
    }

    isPaymentInstallments() {
        return this.type === PolicyPgType.PG_INSTALLMENTS || (this.type === null && this.installmentList?.length > 1);
    }

    isPaymentSubscription() {
        return this.type === PolicyPgType.PG_SUBSCRIPTION;
    }

    getPaymentLabel() {
        if (this.isPaymentSubscription()) {
            // Polisa abonamentowe
            return 'LABELS.PAYMENT_SUBSCRIPTION';
        } else if (this.isPaymentOnce()) {
            // Polisa z płatnością jednorazową
            return 'LABELS.PAYMENT_ONCE';
        } else {
            // Polisa z płatnością ratalną i pozostałe przypadki
            return 'LABELS.PAYMENT_INSTALLMENTS';
        }
    }
}


export class PolicyRisk extends GenericEntity {
    sumInsured: number;
    premium: number;
    insuranceStartDate: string;
    insuranceEndDate: string;
    risk: DictionaryItem;
}

export class PaymentDefinition {
    frequency: DictionaryItem;
    bankAccountNumber: string;
}

export class Policyholder extends GenericEntity {
    party: Party;
}

export class InsuredPerson extends GenericEntity {
    party: Party;
}

export class PgTerminationRequest {
    effectiveDate: string;
    reason: DictionaryItem;
    refundBankAccount?: string;
}

export enum PolicyOperation {
    DUPLICATE_PRINTOUT = 'DUPLICATE_PRINTOUT',
    DOCUMENT_UPLOAD = 'DOCUMENT_UPLOAD',
    CANCELLATION = 'CANCELLATION',
    TERMINATION = 'TERMINATION',
    PERSONAL_DATA_CHANGE = 'PERSONAL_DATA_CHANGE',
    BENEFICIARY_CHANGE = 'BENEFICIARY_CHANGE',
    POLICY_PRINTOUT = 'POLICY_PRINTOUT',
    // TODO: check operations
    PAYMENT_CONFIRMATION = 'PAYMENT_CONFIRMATION',
    REACTIVATION = 'REACTIVATION',
    OWU_DOWNLOAD = 'OWU_DOWNLOAD',
    KID_DOWNLOAD = 'KID_DOWNLOAD',
    PRINTOUT_DOWNLOAD = 'PRINTOUT_DOWNLOAD',
    LOSS_RATIO_DOWNLOAD = 'LOSS_RATIO_DOWNLOAD',
    POLICY_PRINTOUT_APPROVAL = 'POLICY_PRINTOUT_APPROVAL',
}

export class PolicyIdentity {
    id: number;
    policyVersionId: number;
    issueDate: string;
    number: string;
    status: string;
    product: ProductIdentity;
    variant: ProductVariantIdentity;
    insuranceStartDate: string;
    insuranceEndDate: string;
    premium: number;
    partner: PartnerIdentity;
    seller: SellerIdentity;
}

export class SellerIdentity {
    fullName: string;
}

export class PolicyTemplateListItem {
    createdAt: string;
    deleted: boolean;
    filename: string;
    id: number;
    name: string;
    uid: number;
    updatedAt: string;
    userId: number;
    version: number;
}

export class PgPolicyVariant {
    isMobile: boolean;
    ownContributionValue: number;
    ownContributionDescription: string;
    productGroup: DictionaryItem;
}

export class PolicyCoAcquisition {
    coAcquisitionId: number;
    partnerId: number;
    paymentType: DictionaryItem;
    valueType: DictionaryItem;
    rate: number;
    value: number;
}

export enum PolicyPgType {
    PG_SINGLE = 'PG_SINGLE',
    PG_INSTALLMENTS = 'PG_INSTALLMENTS',
    PG_SUBSCRIPTION = 'PG_SUBSCRIPTION',
}
